import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaGripHorizontal, FaTimes } from 'react-icons/fa';
import { formatMeetingRemaining, THREE_MINUTE_WARNING_SECONDS } from '../../utils/meetingEndCountdown';
import { clampOverlayPosition } from '../../utils/viewportOverlay';

const MeetingEndCountdownAlert = ({
  remainingSeconds,
  warningKey = '',
  onDismissWarning,
  meetingTitle = '',
}) => {
  const remaining = Number(remainingSeconds);
  const inFinalWindow = Number.isFinite(remaining) &&
    remaining >= 0 &&
    remaining <= THREE_MINUTE_WARNING_SECONDS;
  const [timerMinimized, setTimerMinimized] = useState(false);
  const [timerPosition, setTimerPosition] = useState(null);
  const [dialogPosition, setDialogPosition] = useState(null);
  const timerRef = useRef(null);
  const dialogRef = useRef(null);
  const dragRef = useRef(null);
  const lastWarningKeyRef = useRef('');

  useEffect(() => {
    if (!warningKey || warningKey === lastWarningKeyRef.current) return;
    lastWarningKeyRef.current = warningKey;
    setTimerMinimized(false);
    setTimerPosition(null);
    setDialogPosition(null);
  }, [warningKey]);

  useEffect(() => {
    if (!warningKey) return undefined;
    const timer = window.setTimeout(() => onDismissWarning?.(), 10000);
    return () => window.clearTimeout(timer);
  }, [onDismissWarning, warningKey]);

  const clampFor = useCallback((kind, x, y) => {
    const element = kind === 'timer' ? timerRef.current : dialogRef.current;
    const rect = element?.getBoundingClientRect?.() || {};
    return clampOverlayPosition({
      x,
      y,
      width: rect.width || (kind === 'timer' ? 300 : 520),
      height: rect.height || (kind === 'timer' ? 132 : 410),
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      margin: 12,
      topInset: kind === 'timer' ? 48 : 0,
      bottomInset: 16,
    });
  }, []);

  const updatePosition = useCallback((kind, next) => {
    if (kind === 'timer') setTimerPosition(next);
    else setDialogPosition(next);
  }, []);

  const beginDrag = useCallback((kind, event) => {
    if ((typeof event.button === 'number' && event.button !== 0) || event.target.closest?.('button')) return;
    const element = kind === 'timer' ? timerRef.current : dialogRef.current;
    const rect = element?.getBoundingClientRect?.();
    if (!rect) return;
    const initial = clampFor(kind, rect.left, rect.top);
    updatePosition(kind, initial);
    dragRef.current = {
      kind,
      pointerId: event.pointerId,
      pointerX: event.clientX,
      pointerY: event.clientY,
      left: initial.x,
      top: initial.y,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }, [clampFor, updatePosition]);

  const moveDrag = useCallback((event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    updatePosition(
      drag.kind,
      clampFor(
        drag.kind,
        drag.left + event.clientX - drag.pointerX,
        drag.top + event.clientY - drag.pointerY
      )
    );
  }, [clampFor, updatePosition]);

  const endDrag = useCallback((event) => {
    if (dragRef.current?.pointerId !== event.pointerId) return;
    dragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  }, []);

  useEffect(() => {
    const keepInsideViewport = () => {
      setTimerPosition((current) => current
        ? clampFor('timer', current.x, current.y)
        : current);
      setDialogPosition((current) => current
        ? clampFor('dialog', current.x, current.y)
        : current);
    };
    window.addEventListener('resize', keepInsideViewport);
    return () => window.removeEventListener('resize', keepInsideViewport);
  }, [clampFor]);

  if (!inFinalWindow && !warningKey) return null;

  const timerStyle = timerPosition
    ? { left: timerPosition.x, top: timerPosition.y }
    : { left: '50%', top: '4.25rem', transform: 'translateX(-50%)' };
  const dialogStyle = dialogPosition
    ? { left: dialogPosition.x, top: dialogPosition.y }
    : { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' };

  return (
    <div className="pointer-events-none absolute inset-0 z-[255]" data-meeting-end-countdown="true">
      {inFinalWindow && !timerMinimized && (
        <section
          ref={timerRef}
          className="pointer-events-auto absolute w-[calc(100%-1.5rem)] max-w-[310px] overflow-hidden rounded-2xl border border-amber-300/45 bg-slate-950/95 text-center shadow-[0_18px_65px_rgba(245,158,11,0.3)] backdrop-blur-xl"
          style={timerStyle}
          role="timer"
          aria-live="polite"
          aria-label={`${formatMeetingRemaining(remaining)} remaining in this meeting`}
          data-countdown-expanded="true"
          data-countdown-size="prominent"
        >
          <header
            className="flex cursor-move touch-none items-center gap-2 border-b border-amber-300/15 bg-white/5 px-2 py-1 text-amber-100"
            onPointerDown={(event) => beginDrag('timer', event)}
            onPointerMove={moveDrag}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            aria-label="Drag remaining-time alert"
          >
            <FaGripHorizontal aria-hidden="true" />
            <span className="flex-1 text-left text-[10px] font-bold uppercase tracking-[0.18em]">Meeting timer</span>
            <button
              type="button"
              onClick={() => setTimerMinimized(true)}
              className="rounded-full p-1 text-amber-100 hover:bg-white/10"
              aria-label="Minimize remaining-time alert"
              title="Minimize timer"
            >
              <FaTimes size={11} />
            </button>
          </header>
          <div className="px-6 py-4">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-amber-300">
              Scheduled time remaining
            </p>
            <p className="mt-1 font-mono text-4xl font-black tabular-nums text-white sm:text-5xl">
              {formatMeetingRemaining(remaining)}
            </p>
          </div>
        </section>
      )}

      {inFinalWindow && timerMinimized && (
        <button
          type="button"
          onClick={() => setTimerMinimized(false)}
          className="pointer-events-auto absolute right-3 top-[4.25rem] inline-flex items-center gap-2 rounded-full border border-amber-300/45 bg-slate-950/95 px-4 py-2.5 text-sm font-bold text-amber-100 shadow-xl backdrop-blur-xl hover:bg-amber-950"
          aria-label={`Reopen remaining-time alert · ${formatMeetingRemaining(remaining)}`}
          data-countdown-minimized="true"
        >
          <span aria-hidden="true">⏳</span>
          <span className="font-mono text-base font-black tabular-nums">{formatMeetingRemaining(remaining)}</span>
          <span className="text-[10px] uppercase tracking-wider text-amber-300">Reopen</span>
        </button>
      )}

      {warningKey && (
        <div className="absolute inset-0 bg-black/35 backdrop-blur-[2px]">
          <section
            ref={dialogRef}
            className="pointer-events-auto absolute max-h-[calc(100dvh-1.5rem)] w-[calc(100%-1.5rem)] max-w-lg overflow-y-auto rounded-3xl border border-amber-200/45 bg-gradient-to-br from-slate-950 via-slate-900 to-amber-950 text-center text-white shadow-[0_28px_100px_rgba(245,158,11,0.45)]"
            style={dialogStyle}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="meeting-end-warning-title"
          >
            <header
              className="sticky top-0 flex cursor-move touch-none items-center gap-2 border-b border-white/10 bg-slate-950/90 px-3 py-2 text-slate-300 backdrop-blur"
              onPointerDown={(event) => beginDrag('dialog', event)}
              onPointerMove={moveDrag}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              aria-label="Drag three-minute warning"
            >
              <FaGripHorizontal aria-hidden="true" />
              <span className="flex-1 text-left text-xs font-bold uppercase tracking-[0.2em]">Time reminder</span>
              <button
                type="button"
                onClick={() => onDismissWarning?.()}
                className="rounded-full p-1.5 hover:bg-white/10 hover:text-white"
                aria-label="Dismiss three-minute warning"
              >
                <FaTimes />
              </button>
            </header>
            <div className="p-7 pt-5">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-amber-300/50 bg-amber-400/15 text-3xl" aria-hidden="true">
                ⏳
              </div>
              <p className="mt-4 text-xs font-bold uppercase tracking-[0.28em] text-amber-300">Time reminder</p>
              <h2 id="meeting-end-warning-title" className="mt-2 text-3xl font-black sm:text-4xl" data-three-minute-heading-size="prominent">
                3 minutes remaining
              </h2>
              <p className="mx-auto mt-3 max-w-md text-base leading-7 text-slate-200">
                {meetingTitle ? `${meetingTitle} is` : 'This meeting is'} approaching its scheduled end. Please finish the current discussion or use the existing extension prompt if eligible.
              </p>
              <button
                type="button"
                onClick={() => onDismissWarning?.()}
                className="mt-6 rounded-xl bg-amber-400 px-6 py-2.5 text-sm font-bold text-slate-950 transition hover:bg-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-200"
              >
                Continue meeting
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default MeetingEndCountdownAlert;
